/**
 * Audit Logging Utilities
 * 
 * Logs sensitive actions for security and compliance.
 */

import { supabase } from "@/integrations/supabase/client";
import { structuredLogger } from "@/lib/logging";

export type AuditActionType = 
  | 'invite_sent'
  | 'invite_rescinded'
  | 'role_changed'
  | 'org_created'
  | 'org_updated'
  | 'parent_recap_link_created'
  | 'parent_recap_link_revoked'
  | 'player_created'
  | 'player_updated'
  | 'player_deleted'
  | 'impersonation_started'
  | 'impersonation_ended'
  | 'login_attempt'
  | 'password_reset'
  | 'rate_limit_triggered'
  | 'integrity_fix_applied'
  | 'competition_entry_submitted'
  | 'competition_entry_approved'
  | 'competition_entry_rejected'
  | 'competition_entry_withdrawn'
  | 'visibility_grant_created'
  | 'visibility_grant_revoked'
  | 'incident_freeze_activated'
  | 'incident_freeze_released'
  | 'discord_linked'
  | 'discord_unlinked';

export type EntityType = 
  | 'user'
  | 'organization'
  | 'player'
  | 'team'
  | 'evaluation'
  | 'parent_recap_link'
  | 'subscription'
  | 'session'
  | 'player_teams'
  | 'participations'
  | 'player_parents'
  | 'season_snapshots'
  | 'season_summaries'
  | 'competition_entries'
  | 'visibility_grants'
  | 'incident_control';

interface LogAuditEventParams {
  orgId?: string | null;
  actorUserId?: string | null;
  actorRole?: string | null;
  actionType: AuditActionType;
  entityType: EntityType;
  entityId?: string | null;
  metadata?: Record<string, unknown>;
  ipAddress?: string | null;
}

/**
 * Logs an audit event to the database.
 * Fails silently to avoid breaking the main flow.
 */
export async function logAuditEvent({
  orgId,
  actorUserId,
  actorRole,
  actionType,
  entityType,
  entityId,
  metadata = {},
  ipAddress,
}: LogAuditEventParams): Promise<void> {
  try {
    // Use the RPC function for proper security definer context
    // Cast metadata to Json-compatible type
    const { error } = await supabase.rpc('log_audit_event', {
      _org_id: orgId || null,
      _actor_user_id: actorUserId || null,
      _actor_role: actorRole || null,
      _action_type: actionType,
      _entity_type: entityType,
      _entity_id: entityId || null,
      _metadata_json: JSON.parse(JSON.stringify(metadata)),
      _ip_address: ipAddress || null,
    });

    if (error) {
      structuredLogger.error("Audit log failed", { actionType, entityType, error: error.message });
    } else {
      structuredLogger.info("Audit event logged", { actionType, entityType, entityId });
    }
  } catch (err) {
    // Fail silently but log for debugging
    structuredLogger.error("Audit log exception", { error: err instanceof Error ? err.message : "Unknown" });
  }
}

/**
 * Helper to get current user info for audit logging
 */
export async function getAuditContext(): Promise<{
  userId: string | null;
  orgId: string | null;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { userId: null, orgId: null };

    const { data: profile } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('user_id', user.id)
      .maybeSingle();

    return {
      userId: user.id,
      orgId: profile?.organization_id || null,
    };
  } catch {
    return { userId: null, orgId: null };
  }
}
